import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader } from "lucide-react";
import betaAPI from "v2/api";
import Toggle from "v2/views/components/common/Toggle";
import { showToast } from "utils/toast";

/**
 * Superadmins only: what this club's parent sign-up form asks for. Whether
 * the optional research choice is offered, and a link to the club's own
 * privacy notice.
 */
const ParentConsentSettings = ({ workspaceId }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [noticeUrl, setNoticeUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await betaAPI.getWorkspaceConsentSettings({
          workspaceId,
        });
        setSettings(data);
        setNoticeUrl(data.privacy_notice_url);
      } catch (error) {
        console.error("Failed to load consent settings", error);
        showToast.error(t("common.errors.loadFailed"));
      }
    };
    load();
  }, [workspaceId, t]);

  const save = async (changes) => {
    setSaving(true);
    try {
      const { data } = await betaAPI.updateWorkspaceConsentSettings({
        workspaceId,
        settings: changes,
      });
      setSettings(data);
      setNoticeUrl(data.privacy_notice_url);
      showToast.success(t("settings.workspace.parentConsent.saved"));
    } catch (error) {
      console.error("Failed to save consent settings", error);
      showToast.error(
        error?.status === 400
          ? t("settings.workspace.parentConsent.badLink")
          : t("common.errors.saveFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex justify-center p-10">
        <Loader size={20} className="animate-spin text-[#9CA3AF]" />
      </div>
    );
  }

  const noticeChanged = noticeUrl.trim() !== settings.privacy_notice_url;

  return (
    <div className="space-y-6 p-6">
      <p className="text-sm text-[#6B7280]">
        {t("settings.workspace.parentConsent.intro")}
      </p>
      <Toggle
        label={t("settings.workspace.parentConsent.research")}
        hint={t("settings.workspace.parentConsent.researchHint")}
        checked={settings.research_consent_enabled}
        onChange={(on) => !saving && save({ research_consent_enabled: on })}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save({ privacy_notice_url: noticeUrl.trim() });
        }}
      >
        <label
          htmlFor="privacy-notice-url"
          className="block text-[13px] font-medium text-[#374151]"
        >
          {t("settings.workspace.parentConsent.notice")}
        </label>
        <p className="mt-0.5 text-[11px] text-[#9CA3AF]">
          {t("settings.workspace.parentConsent.noticeHint")}
        </p>
        <div className="mt-2 flex gap-2">
          <input
            id="privacy-notice-url"
            type="url"
            value={noticeUrl}
            onChange={(e) => setNoticeUrl(e.target.value)}
            placeholder="https://"
            className="min-w-0 flex-1 rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm text-[#111827] outline-none focus:border-[#E07A3A]"
          />
          <button
            type="submit"
            disabled={saving || !noticeChanged}
            className="rounded-lg bg-[#E07A3A] px-4 py-2 text-sm font-medium text-white transition duration-150 hover:bg-[#C96A2E] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("common.actions.save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ParentConsentSettings;
